import { Stack } from '@mui/material';

import { TableShell } from './TableShell';
import { VariableSourceList } from './VariableSourceList';
import { AddVariablePickerModal } from '../containers/definition/AddVariablePickerModal';
import { AnalyticalDatasetResults } from '../containers/results/AnalyticalDatasetResults';
import { AnalyticalDatasetResultsEmpty } from '../containers/results/AnalyticalDatasetResultsEmpty';
import type { TableDataPreviewProps } from '../types';

// Renders the designed table view (TableShell) when a real table is
// selected -- filled with the latest run's cells when results exist for it,
// definition-only otherwise. The "Analytical dataset" option has no table
// shape of its own: it always offers both its structural variable listing
// and its results grid via the List/Table toggle (in the selector row), defaulting
// to whichever one `useTablesSection` picks (Table once results exist, List
// otherwise). Selecting Table before results exist shows an empty grid shaped like
// the real one (same columns, no rows) instead.
export const TableDataPreview = ({
  selectedTable,
  selectedDefinitionTable,
  selectedTableNumber,
  isAnalyticalDatasetSelected,
  selectedDatasetSources,
  sources,
  projectId,
  variablesTitle,
  isVariablePickerOpen,
  onCloseVariablePicker,
  analyticalDatasetProfileId,
  hasAnalyticalDatasetResults,
  datasetSourcesMissingFromRun,
  datasetView,
  datasetEmptyHint,
}: TableDataPreviewProps) => {
  if (!selectedTable && !isAnalyticalDatasetSelected) return null;

  if (selectedTable) {
    return (
      <TableShell
        table={selectedTable}
        tableNumber={selectedTableNumber}
        sources={sources}
        definitionTable={selectedDefinitionTable}
      />
    );
  }

  const isShowingResultsGrid =
    datasetView === 'table' && hasAnalyticalDatasetResults && Boolean(analyticalDatasetProfileId);

  return (
    <Stack spacing={4} sx={datasetView === 'table' ? { flex: 1, minHeight: 0 } : undefined}>
      {datasetView === 'table' ? (
        isShowingResultsGrid && analyticalDatasetProfileId ? (
          <AnalyticalDatasetResults
            projectId={projectId}
            variablesTitle={variablesTitle}
            analyticalDatasetProfileId={analyticalDatasetProfileId}
            hasAnalyticalVariables={selectedDatasetSources.length > 0}
            datasetSources={selectedDatasetSources}
            sourcesMissingFromRun={datasetSourcesMissingFromRun}
          />
        ) : (
          <AnalyticalDatasetResultsEmpty
            variablesTitle={variablesTitle}
            datasetSources={selectedDatasetSources}
            message={datasetEmptyHint}
          />
        )
      ) : (
        <VariableSourceList datasetSources={selectedDatasetSources} title={variablesTitle} />
      )}
      {isVariablePickerOpen && (
        <AddVariablePickerModal projectId={projectId} onClose={onCloseVariablePicker} />
      )}
    </Stack>
  );
};
